<template>
  <div class="app" :class="{ 'is-coarse': vp.coarse }">
    <BackDrop />
    <SpotLight v-if="!vp.coarse && !vp.reduced" />

    <ProgressRail :chapters="chapters" :active="activeId" />

    <header class="topbar">
      <a class="brand" href="#top">
        <span class="brand__mark" aria-hidden="true"></span>
        <span class="brand__text">MAKE&nbsp;IT&nbsp;LOOK&nbsp;REAL</span>
      </a>
      <nav class="topnav">
        <a v-for="c in chapters" :key="c.id" :href="'#' + c.id" :class="{ on: activeId === c.id }">
          {{ c.num }}
        </a>
      </nav>
      <span class="topbar__pct mono">{{ Math.round(vp.progress * 100) }}%</span>
    </header>

    <main id="top">
      <HeroScene />
      <ChSwitch id="switch" />
      <ChKit id="kit" />
      <ChShapes id="shapes" />
      <ChValues id="values" />
      <ChSphere id="sphere" />
      <ChFeatures id="features" />
      <ChProportion id="proportion" />
      <ChHair id="hair" />
      <ChExpression id="expression" />
      <ChPractice id="practice" />
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { startViewport, useViewport } from './composables/useViewport'

import BackDrop from './components/ui/BackDrop.vue'
import SpotLight from './components/ui/SpotLight.vue'
import ProgressRail from './components/ui/ProgressRail.vue'
import SiteFooter from './components/ui/SiteFooter.vue'

import HeroScene from './components/sections/HeroScene.vue'
import ChSwitch from './components/sections/ChSwitch.vue'
import ChKit from './components/sections/ChKit.vue'
import ChShapes from './components/sections/ChShapes.vue'
import ChValues from './components/sections/ChValues.vue'
import ChSphere from './components/sections/ChSphere.vue'
import ChFeatures from './components/sections/ChFeatures.vue'
import ChProportion from './components/sections/ChProportion.vue'
import ChHair from './components/sections/ChHair.vue'
import ChExpression from './components/sections/ChExpression.vue'
import ChPractice from './components/sections/ChPractice.vue'

startViewport()
const vp = useViewport()

const chapters = [
  { id: 'switch', num: '01', label: 'The Switch' },
  { id: 'kit', num: '02', label: 'The Kit' },
  { id: 'shapes', num: '03', label: 'Shape Vision' },
  { id: 'values', num: '04', label: 'Value Engine' },
  { id: 'sphere', num: '05', label: 'Sphere Lab' },
  { id: 'features', num: '06', label: 'Feature Atlas' },
  { id: 'proportion', num: '07', label: 'Face Map' },
  { id: 'hair', num: '08', label: 'Hair' },
  { id: 'expression', num: '09', label: 'Expression' },
  { id: 'practice', num: '10', label: 'The Loop' }
]

const activeId = ref('')
let obs = null

onMounted(() => {
  obs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) activeId.value = e.target.id
      }
    },
    { rootMargin: '-45% 0px -45% 0px' }
  )
  chapters.forEach((c) => {
    const el = document.getElementById(c.id)
    if (el) obs.observe(el)
  })
})

onUnmounted(() => obs && obs.disconnect())
</script>

<style>
.app {
  position: relative;
  min-height: 100vh;
}

/* ---------------- top bar ---------------- */

.topbar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 60;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 var(--gutter);
  background: linear-gradient(180deg, rgba(4, 6, 10, 0.92), rgba(4, 6, 10, 0));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  pointer-events: none;
}
.topbar > * { pointer-events: auto; }

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--v5);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.24em;
}
.brand__mark {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: radial-gradient(circle at 32% 30%, var(--v5) 0 18%, var(--v3) 40%, var(--v1) 88%);
  box-shadow: 0 0 12px rgba(255, 180, 92, 0.45);
}

.topnav {
  margin-left: auto;
  display: flex;
  gap: 2px;
}
.topnav a {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  color: var(--text-faint);
  padding: 6px 8px;
  border-radius: 2px;
  transition: color 0.25s, background 0.25s;
}
.topnav a:hover { color: var(--v4); }
.topnav a.on { color: var(--ink-900); background: var(--signal); }

.topbar__pct {
  color: var(--text-faint);
  min-width: 44px;
  text-align: right;
  font-size: 10px;
}

@media (max-width: 860px) {
  .topnav { display: none; }
  .brand__text { font-size: 10px; }
}
</style>
